import React, { useCallback, useState } from 'react';
import { ButtonNext, ContentHeadingBox, CustomizedTable, MainLayout } from 'src/components';
import plusIcon from 'src/assets/images/icons/new_allocation_plus.svg';

import styles from 'src/components/workspaces-components/styles.module.scss';

import { renderTableContent, WORKSPACES_TABLE_HEADINGS_LIST } from './helpers';
import { useHistory } from 'react-router-dom';
import { DYNAMIC_PAGES } from 'src/constants/pages';

import { titleByAction } from './constants';
import { getCompanyWorkspacesHook } from 'src/hooks/getCompanyWorkspacesHook';
import { useDispatch } from 'react-redux';
import { addWorkspace, deleteWorkspace, editWorkspace } from 'src/redux/actions/workspacesActions';
import { WORKSPACE_ACTIONS } from 'src/constants/workspacesConstants';
import ActionModal from 'src/components/workspaces-components/ActionModal';

const ManagementWorkspacesPage = (): JSX.Element => {
  const { companyWorkspaces, isLoading } = getCompanyWorkspacesHook();

  const dispatch = useDispatch();
  const { push } = useHistory();

  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<null | WORKSPACE_ACTIONS>(null);

  const [actionError, setActionError] = useState<null | string>(null);

  const onToggleActionModal = () => setIsActionModalOpen((prev) => !prev);

  const onCloseActionModal = useCallback(() => {
    setIsActionModalOpen(false);
    setSelectedAction(null);
    setActionError(null);
    setWorkspaceName('');
    setWorkspaceId('');
  }, []);

  const onConfirmAction = useCallback(
    (value?: { name: string }) => {
      const payloadHandlers = {
        onSuccess: () => {
          onCloseActionModal();
        },
        onError: (error) => {
          setActionError(error?.message || 'Something went wrong. Please try again.');
        }
      };
      if (selectedAction === WORKSPACE_ACTIONS.DELETE) {
        dispatch(deleteWorkspace({ id: workspaceId, ...payloadHandlers }));
      }

      if (selectedAction === WORKSPACE_ACTIONS.CREATE_WORKSPACE) {
        const payload = {
          body: { name: value?.name || '' },
          ...payloadHandlers
        };

        dispatch(addWorkspace(payload));
      }

      if (selectedAction === WORKSPACE_ACTIONS.EDIT_WORKSPACE) {
        const payload = {
          id: workspaceId,
          body: { name: value?.name || '' },
          ...payloadHandlers
        };

        dispatch(editWorkspace(payload));
      }
    },
    [dispatch, onCloseActionModal, selectedAction, workspaceId]
  );

  const onActionClick = useCallback((action: WORKSPACE_ACTIONS, workspaceName?: string, workspaceId?: string) => {
    setSelectedAction(action);
    setIsActionModalOpen(true);
    setWorkspaceName(workspaceName);
    setWorkspaceId(workspaceId);
  }, []);

  const onGoToAccess = useCallback(
    (id: string) => {
      push(DYNAMIC_PAGES.WORKSPACES_ACCESS(id));
    },
    [push]
  );

  const tableContent = useCallback(
    (colId, row) => {
      return renderTableContent(colId, row, onGoToAccess, onActionClick);
    },
    [onActionClick, onGoToAccess]
  );

  return (
    <MainLayout isEmptySkeleton>
      <ContentHeadingBox>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>Workspaces</h2>
          <ButtonNext
            label="New Workspace"
            onClick={() => onActionClick(WORKSPACE_ACTIONS.CREATE_WORKSPACE)}
            beforeImg={plusIcon}
            type="button"
          />
        </div>
      </ContentHeadingBox>

      <CustomizedTable
        columns={WORKSPACES_TABLE_HEADINGS_LIST}
        rows={companyWorkspaces}
        onSort={() => undefined}
        isLoading={isLoading}
        guttersRight={45}
        guttersLeft={40}
        getSummaryContent={tableContent}
        hasDetails={false}
        tContainerClass={styles.tContainerClass}
        emptyRowsMsg={'No workspaces found'}
        maxTableContainerHeight="unset"
      />

      <ActionModal
        action={selectedAction}
        isOpen={isActionModalOpen}
        onToggleModal={onToggleActionModal}
        onClose={onCloseActionModal}
        onConfirm={onConfirmAction}
        errorMsg={actionError}
        title={titleByAction[selectedAction]}
        deleteText={`Are you sure you want to delete ${workspaceName} workspace?`}
        onErrorClick={() => setActionError(null)}
        nameValue={workspaceName}
      />
    </MainLayout>
  );
};

export default ManagementWorkspacesPage;
